import { PublishStatus } from "@graffer-inc/cloud-gov-core";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import { useAuthContextV2 } from "../../../../auth-state";
import Analytics from "../../../../common/analytics";
import { MenuItemProps, TextButton } from "@graffer-inc/console-design-kit";

export type ProcedureDetailMenuButtonProps = {
  procedure: ProcedureEntity;
  onManageGroupOpen: () => void;
  onPublishSettingModalOpen: () => void;
  onUnPublish: () => void;
  onPauseAccepting: () => void;
  onCopyOpen: () => void;
  onDeleteOpen: () => void;
  onCustomPathOpen: () => void;
  onExportProcedure: () => void;
  onMatrixCodeOpen: () => void;
  onArchiveOpen: () => void;
  onUnarchive: () => void;
};

class ProcedureDetailMenuEntity {
  props: ProcedureDetailMenuButtonProps;
  constructor(props: ProcedureDetailMenuButtonProps) {
    this.props = props;
  }

  /**
   * 公開・受付日時を変更する
   * @param authState
   * @returns
   */
  private getPublishSettingMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.publish",
        "procedures.unpublish",
      ])
    ) {
      return [
        {
          text: "公開・受付日時を変更",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onPublishSettingModalOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "publishSetting",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 受付を一時停止する
   * @param authState
   * @returns
   */
  private getPauseAcceptingMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.update",
        "procedures.publish",
      ])
    ) {
      return [
        {
          text: "受付を一時停止",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onPauseAccepting();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "pauseAccepting",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 公開を終了する
   * @param authState
   * @returns
   */
  private getUnPublishMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.unpublish",
      ])
    ) {
      return [
        {
          text: "公開を終了",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onUnPublish();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "unpublish",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * "管理グループを変更する/管理グループを設定する
   * @param authState
   * @returns
   */
  private getManageGroupMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.manage_groups",
      ])
    ) {
      return [
        {
          text:
            !!this.props.procedure.groupIds &&
            this.props.procedure.groupIds.length > 0
              ? "管理グループを変更"
              : "管理グループを設定",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onManageGroupOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "manageGroup",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 手続きをコピーして新規作成する
   * @param authState
   * @returns
   */
  private getCopyMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      !this.props.procedure?.isGrafferManaged &&
      this.props.procedure?.isCopiable() &&
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.create",
      ])
    ) {
      return [
        {
          text: "手続きをコピーして新規作成",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onCopyOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "copy",
                );
              }}
            >
              {children}
            </button>
          ),
        } as MenuItemProps,
      ];
    }
    return [];
  }

  /**
   * 手続きを削除する
   * @param authState
   * @returns
   */
  private getDeleteMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.delete",
      ])
    ) {
      return [
        {
          text: "手続きを削除",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onDeleteOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "delete",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 申請開始ページのURLを設定する
   * @returns
   */
  private getCustomPathMenu(): MenuItemProps[] {
    return [
      {
        text: "申請開始ページURLを予約",
        anchor: ({ children }) => (
          <button
            onClick={() => {
              this.props.onCustomPathOpen();
              Analytics.procedureDetailPullDownSelect(
                {
                  id: this.props.procedure.id,
                  name: this.props.procedure.name,
                },
                "customPath",
              );
            }}
          >
            {children}
          </button>
        ),
      },
    ];
  }

  /**
   * 手続きをエクスポートする
   * @param authState
   * @returns
   */
  private getExportMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.create",
      ])
    ) {
      return [
        {
          text: "",
          anchor: ({ children }) => (
            <TextButton
              onClick={() => {
                this.props.onExportProcedure();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "export",
                );
              }}
              text="手続きをエクスポート"
              icon="DownloadIcon"
            >
              {children}
            </TextButton>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 申請開始ページの二次元コードを生成する
   * @param authState
   * @returns
   */
  private getMatrixCodeMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "graffer_utility_tools.generate_matrix_code",
      ])
    ) {
      return [
        {
          text: "申請開始ページの二次元コードを生成",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onMatrixCodeOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "matrixCode",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * 手続きをアーカイブする
   * @param authState
   * @returns
   */
  private getArchiveMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.update",
      ])
    ) {
      return [
        {
          text: "手続きをアーカイブ",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onArchiveOpen();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "archive",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  /**
   * アーカイブされた手続きを復元する
   * @param authState
   * @returns
   */
  private getUnarchiveMenu(
    authState: ReturnType<typeof useAuthContextV2>,
  ): MenuItemProps[] {
    if (
      authState.auth.checkGroupPermission(this.props.procedure, [
        "procedures.update",
      ])
    ) {
      return [
        {
          text: "手続きを復元",
          anchor: ({ children }) => (
            <button
              onClick={() => {
                this.props.onUnarchive();
                Analytics.procedureDetailPullDownSelect(
                  {
                    id: this.props.procedure.id,
                    name: this.props.procedure.name,
                  },
                  "unarchive",
                );
              }}
            >
              {children}
            </button>
          ),
        },
      ];
    }
    return [];
  }

  private hideGroup({
    title,
    menuItems,
  }: {
    title: string;
    menuItems: MenuItemProps[];
  }) {
    return menuItems.length > 0
      ? [
          {
            title,
            menuItems,
          },
        ]
      : [];
  }

  getMenus(authState: ReturnType<typeof useAuthContextV2>): {
    [key in PublishStatus]: {
      title: string;
      menuItems: MenuItemProps[];
    }[];
  } {
    return {
      DRAFT: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getDeleteMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      BEFORE_PUBLISH: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getUnPublishMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      BEFORE_ACCEPTING: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getUnPublishMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      ACCEPTING: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getPauseAcceptingMenu(authState),
            ...this.getUnPublishMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      PAUSING_ACCEPT: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getUnPublishMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      FINISHED_ACCEPTING: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getUnPublishMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
      ],
      UNPUBLISHED: [
        ...this.hideGroup({
          title: "編集",
          menuItems: [
            ...this.getCopyMenu(authState),
            ...this.getExportMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "公開設定",
          menuItems: [
            ...this.getPublishSettingMenu(authState),
            ...this.getCustomPathMenu(),
            ...this.getMatrixCodeMenu(authState),
          ],
        }),
        ...this.hideGroup({
          title: "管理者機能",
          menuItems: [...this.getManageGroupMenu(authState)],
        }),
        ...this.hideGroup({
          title: "アーカイブ",
          menuItems: [...this.getArchiveMenu(authState)],
        }),
      ],
    };
  }

  getArchivedProcMenu(authState: ReturnType<typeof useAuthContextV2>): {
    title: string;
    menuItems: MenuItemProps[];
  }[] {
    return [
      ...this.hideGroup({
        title: "アーカイブ",
        menuItems: [...this.getUnarchiveMenu(authState)],
      }),
    ];
  }
}

export function useMenuButton(
  props: Omit<ProcedureDetailMenuButtonProps, "procedure"> & {
    procedure?: ProcedureDetailMenuButtonProps["procedure"];
  },
) {
  const authState = useAuthContextV2();

  const procedure = props.procedure;

  if (!procedure) return [];

  const entity = new ProcedureDetailMenuEntity({
    ...props,
    procedure,
  });

  if (procedure.isArchived()) {
    return entity.getArchivedProcMenu(authState);
  }

  const menus = entity.getMenus(authState)[procedure.publishStatus];

  return menus;
}
